import { HighlightableText } from '@/components/HighlightableText';
import { HighlightableRichText } from '@/components/HighlightableText/HighlightableRichText';
import { HoverDisplay } from '@/components/HoverDisplay';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { RestrictedContentManager } from '@/pages/PatientTimelinePage/RestrictedContentManager';
import { changeResourceConfidentiality } from '@/services/permissions';
import { CommunicationDirection, ConfidentialityLevel } from '@bariendo/lib/constants';
import { ActionIcon, Anchor, Box, Flex, Group, Menu, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { CardMenuDetail } from './CardMenu';
import { buildPhoneCardDetails } from '@/services/Timeline/phoneCardDetails';
import { TimelineItemType } from '../types';
import { formatHumanName, getReferenceString, MedplumClient, ProfileResource } from '@medplum/core';
import { Attachment, Communication, Reference, Resource } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconArrowUpRight, IconClock, IconFilePlus, IconLink, IconLock, IconUserShare } from '@tabler/icons-react';
import { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { TimelineNotes } from './TimelineNotes';
import { TimelineNoteInput } from './TimelineNoteInput';
import { TimelineChatCommunication, TimelineGroup, TimelineNote, TimelineSmsCommunication } from '../types';
import { TimelineCommunicationResource } from '@/pages/PatientTimelinePage/graphql';
import { AssigneeAvatar } from './AssigneeAvatar';
import { MessageSenderAvatar } from './MessageSenderAvatar';
import { CARD_MENU_ICON_COLOR, CardMenu } from './CardMenu';
import { HighlightableItem } from './HighlightableItem';
import { TimelineAttachments } from './TimelineAttachments';
import { TimelineCard } from './TimelineCard';
import { changeResourcePriority } from '@/services/priority-management';
import { PriorityIcon } from '@/components/PriorityIcon';
import { DateTimeTooltip } from './DateTimeTooltip';
import { JSONContent } from '@tiptap/react';

/** Stable empty details, so a chat row does not hand CardMenu a fresh array
 * on every render. */
const EMPTY_DETAILS: CardMenuDetail[] = [];

/** A row's reassign pair — icon and avatar sharing one picker. Its own
 * component because the state is per row, and rows render inside a `.map`. */
function MessageAssigneeControls({ communication }: { communication: TimelineCommunicationResource }): ReactElement {
  const [reassignOpen, setReassignOpen] = useState(false);

  return (
    <>
      <HoverDisplay.Target>
        <Tooltip label="Reassign">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Reassign"
            style={{ flexShrink: 0 }}
            onClick={() => setReassignOpen(true)}
          >
            <IconUserShare size={18} />
          </ActionIcon>
        </Tooltip>
      </HoverDisplay.Target>
      <AssigneeAvatar communication={communication} opened={reassignOpen} onOpenedChange={setReassignOpen} />
    </>
  );
}

interface TextMessageTimelineItemProps {
  group: TimelineGroup;
  notes?: TimelineNote[];
  highlightRef?: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  /** A header that needs nothing from the card. */
  header?: ReactNode;
  /** A header that acts on the thread, handed the rows' own note plumbing so
   * a card-level "Add sticky note" reuses this card's single input. */
  renderHeader?: (args: {
    onAddNote: (messageReference: Reference<Communication>) => void;
    noteInputTarget: string | null;
  }) => ReactNode;
  searchTerm?: string;
}

export function TextMessageTimelineItem({
  group,
  notes = [],
  highlightRef,
  copyDirectLink,
  hasActiveFilters,
  handleNavigateToItem,
  header,
  renderHeader,
  searchTerm,
}: TextMessageTimelineItemProps) {
  const medplum = useMedplum();
  const {
    currentUser,
    currentUserPermissions: { canAccessRestrictedContent },
  } = useCurrentUser();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showNoteInput, setShowNoteInput] = useState<string | null>(null);

  const shouldShowToggle = group.items.length > 3;

  /**
   * Each row's menu names the two ends of THAT message; chat has none.
   * Built once per group, not per row per render: formatting runs
   * libphonenumber's parser, for a menu that is closed nearly always.
   */
  const detailsByMessage = useMemo(() => {
    const byReference = new Map<string, CardMenuDetail[]>();
    for (const item of group.items) {
      if (item.type !== TimelineItemType.SMS) {
        continue;
      }
      const key = getReferenceString(item.reference);
      if (key) {
        byReference.set(
          key,
          buildPhoneCardDetails({ patientNumber: item.patientNumber, orbitNumbers: [item.orbitNumber] })
        );
      }
    }
    return byReference;
  }, [group.items]);
  const messagesToShow = isExpanded ? group.items : group.items.slice(0, 3);

  const changeResourceRestriction = useCallback(
    async (resourceRef: Reference<Resource>, confidentialityLevel: ConfidentialityLevel) => {
      await changeResourceConfidentiality(medplum, {
        resourceRef,
        confidentialityLevel,
      });
    },
    [medplum]
  );

  const handleAddNote = (messageReference: Reference<Communication>) => {
    setShowNoteInput(getReferenceString(messageReference) ?? null);
  };

  const hideNoteInput = () => {
    setShowNoteInput(null);
  };

  const getNotesForMessage = (messageReference: Reference<Communication>): TimelineNote[] => {
    return notes.filter((note) => getReferenceString(note.linkedTo) === getReferenceString(messageReference));
  };

  const getMessageBackgroundColor = (item: any) => {
    if (item.status === 'not-done' || item.status === 'stopped') {
      return '#f7e8e9de';
    }
    return '#ffffff';
  };

  const getDisplayName = (item: any) => {
    const isFromCurrentUser =
      currentUser && item.sender
        ? getReferenceString(item.sender as ProfileResource) === getReferenceString(currentUser)
        : false;

    return isFromCurrentUser ? 'You' : item.sender ? formatHumanName(item.sender.name?.[0]) : 'Unknown sender';
  };

  const handleChangePriority = async (
    item: TimelineSmsCommunication | TimelineChatCommunication,
    priority: 'urgent' | undefined
  ) => {
    await changeResourcePriority(medplum, item.reference, priority);
  };

  const renderTextMessage = (item: TimelineSmsCommunication | TimelineChatCommunication, index: number) => {
    const messageNotes = getNotesForMessage(item.reference);
    const isShowingNoteInput = showNoteInput === getReferenceString(item.reference);
    const isPatientMessage = item.direction === CommunicationDirection.INCOMING;
    const displayName = getDisplayName(item);

    const mediaAttachments = item.mediaAttachments;
    const pdfAttachments = item.pdfAttachments;
    const hasMultipleImages = (mediaAttachments?.length && mediaAttachments.length > 1) || false;
    const isRestricted = item.confidentiality === ConfidentialityLevel.RESTRICTED;

    // Use mapped rich text content when present (for chat items)
    const richTextContent = (item as TimelineChatCommunication).richTextContent;

    const messageDetails = detailsByMessage.get(getReferenceString(item.reference) ?? '') ?? EMPTY_DETAILS;

    return (
      <Stack key={`${getReferenceString(item.reference)}-${index}`} gap="xs">
        {/* Message Bubble */}
        <Flex w="100%" justify={isPatientMessage ? 'flex-start' : 'flex-end'} gap="xs" align="flex-start">
          <HoverDisplay>
            <HighlightableItem
              maw="60%"
              miw={hasMultipleImages ? '350px' : '300px'}
              itemRef={getReferenceString(item.reference) ?? ''}
              highlightRef={highlightRef}
              style={{ justifySelf: isPatientMessage ? 'flex-start' : 'flex-end' }}
            >
              <Box
                p="md"
                ml={isPatientMessage ? 0 : 'auto'}
                mr={isPatientMessage ? 'auto' : 0}
                style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '12px',
                }}
                bg={getMessageBackgroundColor(item)}
              >
                <Group gap="xs" mb={8} justify="space-between" wrap="nowrap" align="center">
                  <Flex gap="xs" align="center" style={{ minWidth: 0, flex: 1 }}>
                    {/* Avatar for all messages (left side) */}
                    <MessageSenderAvatar
                      sender={item.sender || (item.isAssignable ? { resourceType: 'Patient' } : currentUser)}
                      style={{ flexShrink: 0, marginTop: '4px' }}
                    />
                    <Text
                      variant="section"
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '150px',
                      }}
                    >
                      {displayName}
                    </Text>
                    <DateTimeTooltip date={item.timestamp} />
                    {isRestricted && (
                      <Tooltip label="This message contains sensitive information">
                        <IconLock style={{ minWidth: '16px' }} size={16} />
                      </Tooltip>
                    )}
                    <PriorityIcon priority={item.priorityStatus} />
                  </Flex>

                  <Flex gap="xs" align="center" justify="center" style={{ flexShrink: 0 }}>
                    <HoverDisplay.Target>
                      <CardMenu aria-label="More message actions" details={messageDetails}>
                        <Menu.Item
                          leftSection={<IconFilePlus size={18} color={CARD_MENU_ICON_COLOR} />}
                          onClick={() => handleAddNote(item.reference)}
                          disabled={isShowingNoteInput}
                        >
                          Add sticky note
                        </Menu.Item>
                        {!isRestricted && canAccessRestrictedContent && (
                          <Menu.Item
                            leftSection={<IconLock size={18} color={CARD_MENU_ICON_COLOR} />}
                            onClick={() => changeResourceRestriction(item.reference, ConfidentialityLevel.RESTRICTED)}
                          >
                            Mark as sensitive
                          </Menu.Item>
                        )}
                        {isRestricted && canAccessRestrictedContent && (
                          <Menu.Item
                            leftSection={<IconLock size={18} color={CARD_MENU_ICON_COLOR} />}
                            onClick={() => changeResourceRestriction(item.reference, ConfidentialityLevel.NORMAL)}
                          >
                            Mark as not sensitive
                          </Menu.Item>
                        )}
                        <Menu.Item
                          leftSection={<IconLink size={18} color={CARD_MENU_ICON_COLOR} />}
                          onClick={() => copyDirectLink(getReferenceString(item.reference) ?? '')}
                        >
                          Copy link
                        </Menu.Item>
                        {item.isAssignable && item.priorityStatus !== 'urgent' && (
                          <Menu.Item
                            leftSection={<IconClock size={18} color={CARD_MENU_ICON_COLOR} />}
                            onClick={() => handleChangePriority(item, 'urgent')}
                          >
                            Mark as urgent
                          </Menu.Item>
                        )}
                        {item.isAssignable && item.priorityStatus === 'urgent' && (
                          <Menu.Item
                            leftSection={<IconClock size={18} color={CARD_MENU_ICON_COLOR} />}
                            onClick={() => handleChangePriority(item, undefined)}
                          >
                            Mark as not urgent
                          </Menu.Item>
                        )}
                      </CardMenu>
                    </HoverDisplay.Target>

                    {isPatientMessage && <MessageAssigneeControls communication={item.resource} />}

                    {hasActiveFilters && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => handleNavigateToItem(getReferenceString(item.reference) ?? '')}
                        title="See full context"
                      >
                        <IconArrowUpRight size={16} />
                      </ActionIcon>
                    )}
                  </Flex>
                </Group>
                <TextMessageContent
                  medplum={medplum}
                  item={item}
                  richTextContent={richTextContent}
                  searchTerm={searchTerm}
                  mediaAttachments={mediaAttachments}
                  pdfAttachments={pdfAttachments}
                />
              </Box>
            </HighlightableItem>
          </HoverDisplay>
        </Flex>
        {/* Note Input */}
        {isShowingNoteInput && (
          <Flex w="100%" justify={isPatientMessage ? 'flex-start' : 'flex-end'}>
            <Box maw="60%" miw="250px" w="100%" ml={isPatientMessage ? 0 : 'auto'} mr={isPatientMessage ? 'auto' : 0}>
              <TimelineNoteInput
                timelineItemReference={item.reference}
                onCancel={hideNoteInput}
                onSave={hideNoteInput}
              />
            </Box>
          </Flex>
        )}

        {/* Timeline Notes */}
        {messageNotes.length > 0 && (
          <Flex w="100%" justify={isPatientMessage ? 'flex-start' : 'flex-end'}>
            <Box maw="60%" miw="250px" w="100%" ml={isPatientMessage ? 0 : 'auto'} mr={isPatientMessage ? 'auto' : 0}>
              <TimelineNotes
                notes={messageNotes}
                highlightRef={highlightRef}
                copyDirectLink={copyDirectLink}
                hasActiveFilters={hasActiveFilters}
                handleNavigateToItem={handleNavigateToItem}
                searchTerm={searchTerm}
              />
            </Box>
          </Flex>
        )}
      </Stack>
    );
  };

  return (
    <TimelineCard
      header={renderHeader ? renderHeader({ onAddNote: handleAddNote, noteInputTarget: showNoteInput }) : header}
    >
      <Stack gap="md">
        {messagesToShow.map((item: any, index: any) => renderTextMessage(item, index))}

        {shouldShowToggle && (
          <Box>
            <UnstyledButton onClick={() => setIsExpanded(!isExpanded)} c="#60A5FA" fz="sm" fw={500}>
              {isExpanded ? 'Show less...' : 'Show more...'}
            </UnstyledButton>
          </Box>
        )}
      </Stack>
    </TimelineCard>
  );
}

type TextMessageContentProps = {
  medplum: MedplumClient;
  item: TimelineSmsCommunication | TimelineChatCommunication;
  richTextContent?: JSONContent;
  searchTerm?: string;
  mediaAttachments?: Attachment[];
  pdfAttachments?: Attachment[];
};

function TextMessageContent({
  medplum,
  item,
  richTextContent,
  searchTerm,
  mediaAttachments,
  pdfAttachments,
}: TextMessageContentProps) {
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  if (item.status === 'stopped' && (item.errorCode || item.errors?.length)) {
    const errors =
      item.errors && item.errors.length > 0
        ? item.errors
        : item.errorCode
          ? [{ errorCode: item.errorCode, errorMessage: item.errorMessage }]
          : [];

    return (
      <Box>
        <HighlightableText
          text={item.content ?? ''}
          searchTerm={searchTerm ?? ''}
          c="dark"
          lh={1.6}
          style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}
        />
        <Text style={{ fontWeight: '600' }}>Error sending SMS.</Text>
        {showErrorDetails && (
          <Stack gap="xs" style={{ marginTop: '8px' }}>
            {errors.map((error, index) => (
              <Text key={index}>
                Error Code: {error.errorCode || 'Unknown'}
                {error.errorMessage && ` - ${error.errorMessage}`}
              </Text>
            ))}
          </Stack>
        )}
        <Anchor
          component="button"
          onClick={() => setShowErrorDetails(!showErrorDetails)}
          style={{ fontSize: '14px', marginTop: '4px' }}
        >
          {showErrorDetails ? 'Show Less...' : 'See full Error'}
        </Anchor>
      </Box>
    );
  }

  return (
    <RestrictedContentManager isRestricted={item.hiddenPayload}>
      {richTextContent ? (
        <HighlightableRichText
          richTextContent={richTextContent}
          text={item.content ?? ''}
          searchTerm={searchTerm ?? ''}
          c="dark"
          lh={1.6}
          style={{ wordBreak: 'break-word' }}
        />
      ) : (
        <HighlightableText
          text={item.content ?? ''}
          searchTerm={searchTerm ?? ''}
          c="dark"
          lh={1.6}
          style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}
        />
      )}

      <TimelineAttachments
        medplum={medplum}
        mediaAttachments={mediaAttachments || []}
        pdfAttachments={pdfAttachments || []}
      />
    </RestrictedContentManager>
  );
}
